import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '../../../redux/hooks';
import { fetchSettings } from '../../../redux/slices/AccountLevelSlice';
import MyButton from '../../../shared-components/button/Button';
import InputWithDropDown from '../../../shared-components/input-with-drop-down/InputWithDropDown';
import feeConfigurationApi from '../../../utils/api/fee-configuration';

interface PlatformLevelInterface {
  _id: string;
  platformFee: string;
  platformFeeType: 'amount' | 'percentage';
  sessionFee: string;
  sessionFeeType: 'amount' | 'percentage';
}

/**
 * Platform level component
 */
function PlatformLevel() {
  const { t, i18n } = useTranslation('sharedComponent');
  const [data, setData] = useState<PlatformLevelInterface | null>(null);
  const dispatch = useAppDispatch();

  /** Fetch configuration */
  useEffect(() => {
    /**
     * Function for fetching fee configuration data
     */
    const fetchFeeConfiguration = async () => {
      try {
        const response = await feeConfigurationApi.fetchPlatformLevel();
        setData(response.data);
      } catch (error) {
        console.error('Error fetching fee configuration:', error);
      }
    };
    fetchFeeConfiguration();
  }, []);

  /** Generic updater */
  const updateField = useCallback(
    <K extends keyof PlatformLevelInterface>(field: K, value: PlatformLevelInterface[K]) => {
      setData((previous) => (previous ? { ...previous, [field]: value } : previous));
    },
    [],
  );

  /** Save changes */
  const handleSave = async () => {
    if (!data) return;
    try {
      await feeConfigurationApi.updatePlatformLevel({
        platformFee: data.platformFee,
        platformFeeType: data.platformFeeType,
        sessionFee: data.sessionFee,
        sessionFeeType: data.sessionFeeType,
      });
      dispatch(fetchSettings());
    } catch (error) {
      console.error('Error saving fee configuration:', error);
    }
  };

  return (
    <div className="platform-level-wrap" dir={i18n.dir()}>
      <InputWithDropDown
        label={t('platformFee')}
        value={data?.platformFee}
        mode={data?.platformFeeType ?? 'amount'}
        onChangeValue={(value) => updateField('platformFee', value)}
        onChangeMode={(mode) => updateField('platformFeeType', mode)}
      />

      <InputWithDropDown
        label={t('sessionFee')}
        value={data?.sessionFee}
        mode={data?.sessionFeeType ?? 'amount'}
        onChangeValue={(value) => updateField('sessionFee', value)}
        onChangeMode={(mode) => updateField('sessionFeeType', mode)}
      />

      <MyButton label={t('save', 'Save')} buttonType="submit" onClickFunc={handleSave} />
    </div>
  );
}

export default PlatformLevel;
